import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { get } from "nodash"
import { hideGlobalModal, showGlobalModal, snapshot } from "runlevel/actions"
import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { MapSnapshotResult } from "@nova/component-map-snapshot"
import { createWorkspace } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    countryCodeToSupportedRegion,
    defaultUnitsForRegion,
    prettyRegionName,
    SupportedRegions,
} from "@nova/constants"
import { useSWRNoRevalidate } from "@nova/swr"
import { MapPreview } from "features/map/MapPreview"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { formatErrorMessage } from "@nova/util-strings"
import {
    addWorkspaceId,
    selectUserFirstName,
} from "features/entity-caching/members/UserManagementSlice"
import { zoomToArea } from "features/map/control/slice"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { WorkspaceTextInput } from "@nova/component-inline-text-input"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "./types"
import { useRouter } from "pages"
import { closeWorkspaceSwitcher } from "panels/global-nav/slice"
import { useFeatureFlag } from "features/feature-flags/useFeatureFlag"
import { RadioButton } from "@nova/ds-component-radio-button"
import { fetchApiGateway } from "@nova/fetch-api-gateway"

const BETA_REGIONS: SupportedRegions[] = [SupportedRegions.Australia]

type Serializable =
    | { [k: string]: Serializable }
    | string
    | number
    | boolean
    | null
    | undefined
    | { name: string; value: string | boolean }[]

const persistedStateEditor = "persisted-state-editor"
export function usePersistentState<T extends Serializable>(key: string, defaultState: T) {
    key = persistedStateEditor + "-" + key
    const [state, setState] = useState(() => {
        if (localStorage) {
            try {
                const stashed = localStorage.getItem(key)
                if (!stashed) return defaultState

                return JSON.parse(stashed) as T
            } catch {
                console.error("OrganizationEditorModal -- error parsing out of local storage")
                return defaultState
            }
        } else {
            return defaultState
        }
    })

    const setPersistentState = (newValue: T | ((prevState: T) => T)) => {
        localStorage.setItem(
            key,
            JSON.stringify(newValue instanceof Function ? newValue(state) : newValue)
        )
        setState(newValue)
    }
    return {
        state,
        setState: setPersistentState,
        clear: () => {
            localStorage.removeItem(key)
        },
    }
}

export default function OrganizationEditorModal() {
    const dispatch = useAppDispatch()
    const firstName = useAppSelector(selectUserFirstName)
    const rootRouter = useRouter("/")
    const workspaceRouter = useRouter("/workspace/:workspaceId")

    const betaRegionsEnabled = useFeatureFlag("betaRegions")
    const allowedRegions = Object.values(SupportedRegions).filter(
        (region) => betaRegionsEnabled || !BETA_REGIONS.includes(region)
    )

    const id = "new"
    let fullName: string | undefined,
        location:
            | {
                  latitude: number
                  longitude: number
                  zoom: number
              }
            | undefined

    const {
        state: editorValues,
        setState: setEditorValues,
        clear: clearEditorValues,
    } = usePersistentState<{
        fullName?: string
        location?: {
            latitude: number
            longitude: number
            zoom: number
        }
        settings: { name: string; value: string | boolean }[]
    }>(id, {
        fullName,
        location,
        settings: [],
    })
    const selectedRegion = editorValues.settings?.find(
        (setting) => setting.name === "region" && setting.value
    )?.value as SupportedRegions | undefined
    const settingNames = new Set(editorValues.settings?.map((setting) => setting.name))
    const hasAllRequiredSettings = ["region", "distance", "area"].every((name) =>
        settingNames.has(name)
    )

    const modalCloseHandler = useCallback(() => {
        clearEditorValues()
        dispatch(hideGlobalModal())
    }, [clearEditorValues, dispatch])

    const handleFormChange = useCallback(
        (newValue: string, fieldName: string) => {
            setEditorValues((prevValues) => {
                return {
                    ...prevValues,
                    [fieldName]: newValue,
                }
            })
        },
        [setEditorValues]
    )

    const handleRegionChange = useCallback(
        (newRegion: SupportedRegions) => {
            const units = defaultUnitsForRegion[newRegion]
            setEditorValues((prevValues) => {
                const filtered = (prevValues.settings ?? []).filter(
                    (s) => s.name !== "region" && s.name !== "distance" && s.name !== "area"
                )
                return {
                    ...prevValues,
                    settings: [
                        ...filtered,
                        { name: "region", value: newRegion },
                        { name: "distance", value: units.distance },
                        { name: "area", value: units.area },
                    ],
                }
            })
        },
        [setEditorValues]
    )

    const onSnapshotConfirm = useCallback(
        (r: MapSnapshotResult) => {
            const zoom = r.zoom()
            const { latitude, longitude } = r.centerLatLng()
            setEditorValues({
                ...editorValues,
                location: {
                    latitude,
                    longitude,
                    zoom,
                },
            })
            workspaceRouter.navigate({ path: "/" })
            dispatch(initializeCenterModalForComponentAction(ModalNames.OrganizationEditorModal))
            dispatch(showGlobalModal())
        },
        [setEditorValues, editorValues, workspaceRouter, dispatch]
    )

    const onSnapshotCancel = () => {
        workspaceRouter.navigate({ path: "/" })
        dispatch(initializeCenterModalForComponentAction(ModalNames.OrganizationEditorModal))
        dispatch(showGlobalModal())
    }

    const handleFormSubmit = useCallback(async () => {
        const { fullName, location, settings } = editorValues

        if (!fullName || !selectedRegion) {
            dispatch(
                setGlobalNotificationContext({
                    message: formatErrorMessage(!!fullName, !!selectedRegion),
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
            return
        }

        const result = await dispatch(
            createWorkspace({
                fullName,
                location,
                settings,
            })
        )
        const newId = get(result, "payload.organization.id") as unknown as string
        dispatch(addWorkspaceId(newId))
        rootRouter.navigate(
            {
                path: "/workspace/:workspaceId/explore/ws",
                params: { workspaceId: newId },
            },
            { state: "promptInvite" }
        )
        modalCloseHandler()
        dispatch(closeWorkspaceSwitcher())

        const error = get(result, "meta.rejectionReason") as string | undefined
        if (error) {
            console.warn(error)
            return error
        }
    }, [editorValues, selectedRegion, dispatch, rootRouter, modalCloseHandler])

    const { data: geolocation } = useSWRNoRevalidate("geolocation", () =>
        fetchApiGateway("get /geolocation")
    )

    const detectedRegion = useMemo(() => {
        const countryCode = geolocation?.countryCode
        return countryCode ? (countryCodeToSupportedRegion[countryCode] ?? null) : null
    }, [geolocation])

    /** Select the detected region if it hasn't been selected yet. */
    useEffect(() => {
        if (detectedRegion && !selectedRegion && allowedRegions.includes(detectedRegion)) {
            handleRegionChange(detectedRegion)
        }
    }, [allowedRegions, detectedRegion, selectedRegion, handleRegionChange])

    /** Center the starting-area viewport near the user if it hasn't been set yet. */
    useEffect(() => {
        const { lat, lon } = geolocation ?? {}
        if (lat == null || lon == null || editorValues.location) return

        setEditorValues((prevValues) => ({
            ...prevValues,
            location: { latitude: lat, longitude: lon, zoom: 10 },
        }))
    }, [geolocation, editorValues.location, setEditorValues])

    return (
        <HighPriorityNovaModalContainer
            title="Create a Workspace"
            className="w-[30rem]"
            closeModal={modalCloseHandler}
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary title="Cancel" onClick={modalCloseHandler} />
                    <ButtonPrimary
                        title="Create Workspace"
                        onClick={handleFormSubmit}
                        disabled={!hasAllRequiredSettings}
                    />
                </div>
            }
        >
            <h5 className="text-gray-100 mb-2">Workspace Name*</h5>
            <WorkspaceTextInput
                name="fullName"
                value={editorValues.fullName}
                placeholder={
                    firstName + "'" + (firstName?.at(-1) === "s" ? "" : "s") + " Workspace"
                }
                handleChange={(value) => handleFormChange(value, "fullName")}
                className="!h-[2.5rem]"
                autoFocus
                inputClassName="!w-full !h-[2.5rem] !bg-gray-700 !border-none !p-2 !pl-4 !rounded-md focus:ring-blue-200"
            />
            <h5 className="text-gray-100 mb-2 mt-4">Data Residency*</h5>
            <div className="flex flex-col gap-3">
                {allowedRegions.map((region) => {
                    const onClick = () => handleRegionChange(region)
                    return (
                        <div
                            key={region}
                            className="flex gap-3 items-center cursor-pointer hover:text-gray-0 text-gray-100 group"
                            onClick={onClick}
                        >
                            <RadioButton isSelected={selectedRegion === region} onClick={onClick} />
                            <h5>{prettyRegionName[region]}</h5>
                        </div>
                    )
                })}
            </div>
            <h5 className="text-gray-100 mb-2 mt-4">Starting Area</h5>
            <MapPreview
                title="Select Starting Area"
                onClick={() => {
                    dispatch(hideGlobalModal())
                    if (editorValues.location) {
                        dispatch(
                            zoomToArea({
                                longitude: editorValues.location.longitude,
                                latitude: editorValues.location.latitude,
                                zoom: editorValues.location.zoom,
                            })
                        )
                    }
                    dispatch(
                        snapshot({
                            onConfirm: onSnapshotConfirm,
                            onCancel: onSnapshotCancel,
                        })
                    )
                    workspaceRouter.navigate({ path: "/settings/map-snapshot" })
                    return Promise.resolve()
                }}
                viewport={editorValues.location}
                className="!h-[200px] rounded-lg overflow-hidden"
            />
        </HighPriorityNovaModalContainer>
    )
}
